import { Alert, Button, Card, Field, Input, Modal } from "@aman/design-system";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, asApiError, type ApiError } from "../api/client";
import { useApiQuery } from "../api/hooks";
import { downloadApiFile } from "../api/download";
import { ApiErrorAlert, DataTable, LoadingTable, PageHeader, StatusBadge } from "../components/common";
import { VoucherRedeemer } from "../components/VoucherRedeemer";
import { listFromPayload, objectFromPayload, stringValue, type AnyRecord } from "../types";

export function CustomerPortalPage() {
  const { t } = useTranslation();
  const me = useApiQuery<unknown>("/customer/me");
  const licenses = useApiQuery<unknown>("/customer/licenses");
  const entries = useApiQuery<unknown>("/customer/wallet/entries");
  const transfers = useApiQuery<unknown>("/customer/device-transfers");
  const notifications = useApiQuery<unknown>("/customer/notifications");
  const orders = useApiQuery<unknown>("/customer/orders");
  const profile = objectFromPayload(me.data);
  const [selectedLicense, setSelectedLicense] = useState<AnyRecord>();
  const licenseId = selectedLicense === undefined ? "" : stringValue(selectedLicense["publicId"]);
  const devices = useApiQuery<unknown>(licenseId === "" ? null : `/customer/licenses/${encodeURIComponent(licenseId)}/devices`);
  const [releaseDevice, setReleaseDevice] = useState<AnyRecord>();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const [message, setMessage] = useState<string>();

  const reload = async (): Promise<void> => {
    await Promise.all([me.reload(), licenses.reload(), entries.reload(), transfers.reload(), notifications.reload(), orders.reload(), devices.reload()]);
  };
  const downloadInvoice = async (order: AnyRecord): Promise<void> => {
    setBusy(true); setError(undefined);
    try { const id = stringValue(order["publicId"]); await downloadApiFile(`/customer/orders/${encodeURIComponent(id)}/invoice`, `${stringValue(order["invoiceNumber"], id)}.pdf`, "application/pdf"); }
    catch (caught) { setError(asApiError(caught)); } finally { setBusy(false); }
  };
  const renew = async (license: AnyRecord): Promise<void> => {
    setBusy(true); setError(undefined); setMessage(undefined);
    try {
      const result = objectFromPayload(await api.post<unknown>(
        `/customer/licenses/${encodeURIComponent(stringValue(license["publicId"]))}/renew`, { confirm: true }));
      setMessage(t("pages.customerPortal.renewed", {
        amount: stringValue(result["chargedAmount"], "0"), expiry: stringValue(result["expiresAt"], "—"),
      }));
      await reload();
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };
  const release = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (releaseDevice === undefined) return;
    setBusy(true); setError(undefined); setMessage(undefined);
    try {
      const result = objectFromPayload(await api.post<unknown>(
        `/customer/devices/${encodeURIComponent(stringValue(releaseDevice["publicId"]))}/release`, { reason }));
      const pending = result["status"] === "pending_approval";
      setMessage(pending ? t("pages.customerPortal.transferPending") : t("pages.customerPortal.deviceReleased"));
      setReleaseDevice(undefined); setReason("");
      await reload();
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };

  return <div className="page-stack">
    <PageHeader title={t("pages.customerPortal.title")} subtitle={t("pages.customerPortal.subtitle")} />
    <ApiErrorAlert error={me.error ?? licenses.error ?? entries.error ?? transfers.error ?? notifications.error ?? orders.error ?? devices.error ?? error}
      onRetry={() => void reload()} />
    {message !== undefined ? <Alert tone="success">{message}</Alert> : null}
    <section className="portal-kpis">
      <Card><span className="portal-kpi__label">{t("common.balance")}</span><strong className="portal-kpi__value">{stringValue(profile["balance"], "0")}</strong><small>{t("pages.customerPortal.credits")}</small></Card>
      <Card><span className="portal-kpi__label">{t("common.available")}</span><strong className="portal-kpi__value">{stringValue(profile["available"], "0")}</strong><small>{t("pages.customerPortal.reserved", { amount: stringValue(profile["reserved"], "0") })}</small></Card>
      <Card><span className="portal-kpi__label">{t("nav.licenses")}</span><strong className="portal-kpi__value">{stringValue(profile["licenseCount"], "0")}</strong><small>{t("pages.customerPortal.activeDevices", { count: Number(profile["activeDeviceCount"] ?? 0) })}</small></Card>
    </section>
    <VoucherRedeemer onRedeemed={async () => { await Promise.all([me.reload(), entries.reload()]); }} />
    <Card title={t("pages.customerPortal.myLicenses")} bodyClassName="card-body--flush">
      {licenses.loading ? <LoadingTable columns={6} /> : <DataTable rows={listFromPayload(licenses.data)} columns={[
        { key: "productName", label: t("pages.licenses.product") },
        { key: "planName", label: t("pages.licenses.plan") },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "expiresAt", label: t("pages.customerPortal.expiresAt"), render: (row) => stringValue(row["expiresAt"], "—") },
        { key: "activeDevices", label: t("nav.devices") },
      ]} actions={(row) => <><Button type="button" size="small" variant="secondary" onClick={() => setSelectedLicense(row)}>{t("pages.customerPortal.devices")}</Button><Button type="button" size="small" busy={busy} onClick={() => void renew(row)}>{t("pages.licenses.renew")}</Button></>} />}
    </Card>
    {selectedLicense !== undefined ? <Card title={`${t("nav.devices")} — ${stringValue(selectedLicense["productName"])}`}
      action={<Button type="button" variant="ghost" size="small" onClick={() => setSelectedLicense(undefined)}>×</Button>}
      bodyClassName="card-body--flush">
      {devices.loading ? <LoadingTable columns={5} /> : <DataTable rows={listFromPayload(devices.data)} columns={[
        { key: "name", label: t("common.name") }, { key: "platform", label: t("common.type") },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "lastSeenAt", label: t("pages.customerPortal.lastSeen"), render: (row) => stringValue(row["lastSeenAt"], "—") },
      ]} actions={(row) => row["status"] === "active" ? <Button type="button" size="small" variant="danger" onClick={() => setReleaseDevice(row)}>{t("pages.devices.release")}</Button> : null} />}
    </Card> : null}
    <div className="portal-grid">
      <Card title={t("pages.customerPortal.walletHistory")} bodyClassName="card-body--flush"><DataTable rows={listFromPayload(entries.data).slice(0, 10)} columns={[
        { key: "description", label: t("common.description") }, { key: "amount", label: t("common.amount") },
        { key: "createdAt", label: t("common.date") },
      ]} /></Card>
      <Card title={t("pages.devices.transferRequests")} bodyClassName="card-body--flush"><DataTable rows={listFromPayload(transfers.data).slice(0, 10)} columns={[
        { key: "productName", label: t("pages.licenses.product") },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "createdAt", label: t("common.date") },
      ]} /></Card>
    </div>
    <Card title={t("pages.notifications.inApp")} bodyClassName="card-body--flush"><DataTable rows={listFromPayload(notifications.data).slice(0, 20)} columns={[
      { key: "type", label: t("common.type"), render: (row) => t(`events.${stringValue(row["type"])}`, { defaultValue: stringValue(row["type"]) }) },
      { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
      { key: "createdAt", label: t("common.date") },
    ]} /></Card>
    <Card title={t("pages.orders.myInvoices")} bodyClassName="card-body--flush"><DataTable rows={listFromPayload(orders.data).slice(0, 50)} columns={[
      { key: "invoiceNumber", label: t("pages.orders.invoiceNumber") }, { key: "productName", label: t("pages.licenses.product") },
      { key: "planName", label: t("pages.licenses.plan") },
      { key: "displayAmount", label: t("pages.orders.displayPrice"), render: (row) => `${stringValue(row["displayAmount"])} ${stringValue(row["displayCurrency"])}` },
      { key: "chargedAmount", label: t("pages.orders.paidCredits") }, { key: "createdAt", label: t("common.date") },
    ]} actions={(row) => <Button type="button" size="small" variant="secondary" busy={busy} onClick={() => void downloadInvoice(row)}>{t("pages.orders.invoice")}</Button>} /></Card>
    <Modal open={releaseDevice !== undefined} title={t("pages.devices.release")} onClose={() => setReleaseDevice(undefined)}>
      <form className="form-grid" onSubmit={(event) => void release(event)}><Field label={t("common.reason")}><Input required minLength={3} value={reason} onChange={(event) => setReason(event.target.value)} /></Field><Alert tone="warning">{t("pages.customerPortal.releaseWarning")}</Alert><Button type="submit" busy={busy}>{t("pages.devices.release")}</Button></form>
    </Modal>
  </div>;
}
